import { useEffect, useMemo, useState } from "react";
import { MaterialReactTable, MRT_Row, MRT_RowData, type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table';
import dayjs from 'dayjs';

import { Box, Button, Grid, Link, Typography, ListItemIcon, Menu, MenuItem, MenuProps, Paper, alpha, styled, Theme, Switch, Tooltip, Select } from '@mui/material';
import assets from "../../../assets";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CssBaseline from '@mui/material/CssBaseline';
import { ResultSessionStorageKey, ResultSessionStorageModel, ResultTableParameterStorageService } from "../../../services/sessionStorage";
import colorConfigs from "../../../configs/colorConfigs";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { tsXLXS } from 'ts-xlsx-export';
import { MRT_FilterFns } from 'material-react-table';
import { useMediaQuery } from '@mui/material';
import RareButton from "../buttons/RareButton";
import api from "../../../redux/features/auth/axiosAuthHelper";
import { interpretationColours, interpretationLabels } from "../CustomHooks";
import ResultClassLabel from "../buttons/resultClassLabel";
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';

const StyledMenu = styled((props: any) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));


interface ResultsTableProps {
  authData: any;
  rows: any[];
  isLoading: boolean;
  sorting: any;
  // columnVisibility: any;
  columnFilters: any;
  pagination: any;
  globalFilter: any;
  showGlobalFilter: any;
  setShowGlobalFilter: (value: any) => void;
  handleRemoveAllFilters: () => void;
  setGlobalFilter: (value: string) => void;
  setSorting: (sorting: any[]) => void;
  // setColumnVisibility: (columnVisibility: { [key: string]: boolean }) => void;
  setColumnFilters: (filters: any[]) => void;
  setPagination: (pagination: { pageSize: number; pageIndex: number }) => void;
  setResult: (detail: any) => void;
  setPopup: (detail: any) => void;
  handleRefreshButton: () => void;
  openPopup: (row: any) => void;
  onBulkReview: (rows: MRT_Row<MRT_RowData>[]) => void;
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  authData,
  rows,
  isLoading,
  sorting,
  // columnVisibility,
  columnFilters,
  pagination,
  globalFilter,
  showGlobalFilter,
  setShowGlobalFilter,
  setGlobalFilter,
  handleRemoveAllFilters,
  setSorting,
  // setColumnVisibility,
  setColumnFilters,
  setPagination,
  setResult,
  setPopup,
  handleRefreshButton,
  openPopup,
  onBulkReview
}) => {

  console.log("Results", rows)

  const isMobile = useMediaQuery('(max-width:768px)');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSaveFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const customResultTypeFilter = (row: any, columnId: any, filterValue: string) => {
    const { datarun_id, test_configuration } = row.original;
    const isDCMAppResult = !datarun_id && test_configuration?.collection_mode;
    const isNormalAppResult = !datarun_id && !test_configuration?.collection_mode;
    const isDatarunResult = !!datarun_id;

    const datarunText = `Datarun: ${datarun_id}`;
    const searchValue = filterValue.toLowerCase();

    return (
      (isDCMAppResult && "data collection mode result".includes(searchValue)) ||
      (isNormalAppResult && "normal app result".includes(searchValue)) ||
      (isDatarunResult && "reprocessed result".includes(searchValue)) ||
      (isDatarunResult && datarunText.toLowerCase().includes(searchValue))
    );
  };


  const visualResultTextFilterFn = (row: any, columnId: any, filterValue: string) => {
    // Get the visual result data from the row.
    const visualResult = row.original.visual_result
    const isVisualResult = row.original.visual_result?.visual_interpretation ? true : false;
    const searchText = filterValue.toLowerCase();

    // If the user types "pending review", return only rows with a visual result that is NOT reviewed.
    if ("pending".includes(searchText.toLowerCase())) {
      return isVisualResult && !visualResult.is_reviewed;
    }

    // If the user types "reviewed", return only rows with a visual result that IS reviewed.
    if ("reviewed on".includes(searchText.toLowerCase())) {
      return isVisualResult && visualResult.is_reviewed;
    }

    // If no matching filter text is provided, return all rows.
    return true;
  };

  const filterFns = {
    ...MRT_FilterFns,
    customResultTypeFilter,
    visualResultTextFilterFn
  };




  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Result<br />ID
      </div>,
      filterFn: (row, id, filterValue) => {
        const idValue = row.getValue(id);
        // Ensure both values are numbers before comparison
        return idValue !== null && idValue !== undefined && Number(idValue) === Number(filterValue);
      },
    },
    {
      accessorKey: 'timestamp',
      accessorFn: (row) => dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      header: 'Timestamp',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Timestamp<br />&nbsp;
      </div>,
      Cell: ({ cell }) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {cell.getValue()?.toString().split(' ').join('\n')}
        </div>
      ),
    },
    {
      accessorKey: 'test_type',
      header: 'Test Type',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Test<br />Type
      </div>,
    },
    {
      accessorKey: 'sample_id',
      header: 'Sample ID',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Sample<br />ID
      </div>,
    },
    {
      accessorKey: 'final_result_interpretation',
      header: 'Interpretation',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Interpretation<br />&nbsp;
      </div>,
      Cell: ({ cell, row }) => {
        const apiValue = row.original.final_result_interpretation;
        const displayValue = apiValue ? interpretationLabels[apiValue as keyof typeof interpretationLabels] : ''; // Borderline is now BORDERLINE
        const colour = interpretationColours[apiValue as keyof typeof interpretationColours];
        return displayValue ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ width: '100%', display: 'flex' }}>
              <ResultClassLabel labelcolour={colour} value={displayValue} />
            </Box>
          </Box>
        ) : (null)
      },
    },
    {
      accessorKey: 'first_strip_image',
      header: 'Strip Image',
      enableColumnFilter: false,
      enableSorting: false,
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Strip<br />Image
      </div>,
      Cell: ({ row }) => {
        const src = row.original.first_strip_image;
        if (!src) return null;

        return (
          <Box
            sx={{
              // optional: keep cells tidy if some images are very tall
              maxHeight: 56,           // cap the row height if you want
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={src}
              alt=""
              loading="lazy"
              sx={{
                // key bits:
                width: 160,           // fixed width for consistency
                height: 'auto',       // preserve aspect ratio
                display: 'block',
                // optional safety if massive originals:
                maxHeight: '56px',
              }}
            />
          </Box>
        );
      }
    },
    {
      accessorKey: 'visual_result',
      header: 'Visual Interpretation',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Visual<br />Interpretation
      </div>,
      Cell: ({ cell, row }) => {
        const visual_result = row.original.visual_result

        return visual_result ? (
          <Box>
            {visual_result.is_reviewed ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleOutlineOutlinedIcon fontSize="small" color="success" /> <Typography>Reviewed</Typography>
                </Box>
                <Typography>{dayjs(visual_result.reviewed_timestamp).format('YYYY-MM-DD HH:mm:ss')} </Typography>
              </>
            ) : (
              <>
                <Typography>Pending Review</Typography>
              </>
            )
            }
          </Box >
        ) : (null)
      },
      filterFn: visualResultTextFilterFn,
    },
    {
      accessorKey: 'sample_type',
      header: 'Sample Type',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Sample<br />Type
      </div>,
    },
    {
      accessorKey: 'type',
      header: 'Result Type',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Result<br />Type
      </div>,
    },
    {
      accessorKey: 'user_id',
      header: 'User ID',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        User<br />ID
      </div>,
      filterFn: (row, id, filterValue) => {
        const idValue = row.getValue(id);
        // Ensure both values are numbers before comparison
        return idValue !== null && idValue !== undefined && Number(idValue) === Number(filterValue);
      },
    },
    {
      // Combine both organisation_id and organisation_name into a single column
      accessorFn: (row) => `${row.organisation_id} ${row.organisation_name}`,
      id: 'organisation',
      header: 'Organisation',
      Header: () => (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          Organisation<br />ID / Name
        </div>
      ),
      Cell: ({ row }) => {
        const { organisation_id, organisation_name } = row.original;
        return (
          <div>{organisation_id}&nbsp;&nbsp;{organisation_name}</div>
        );
      },
      // Custom filter: convert both fields to a string and check if the filter text is included
      filterFn: (row, id, filterValue) => {
        const value = `${row.original.organisation_id} ${row.original.organisation_name}`.toLowerCase();
        return value.includes(filterValue.toLowerCase());
      },
    },
    {
      accessorKey: 'lot_number',
      header: 'Lot Number',
      Header: () => <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Lot<br />Number
      </div>,
    },
  ]

  // const defaultVisibility = columns.reduce((acc, column) => {
  //   if (column.accessorKey) {
  //     acc[column.accessorKey as string] = true;
  //   }
  //   return acc;
  // }, {} as Record<string, boolean>);

  // // Merge the two objects so that properties from columnVisibility override the default values
  // const currentColumnVisibility = {
  //   ...defaultVisibility,
  //   ...columnVisibility,
  // };


  // Define the type for the keys for handling persistance of table settings in session storage
  type ResultSessionStorageKeyType = keyof ResultSessionStorageModel;

  // Generic handler function for persisting table parameters
  const handleParameterChange = <T,>(
    key: ResultSessionStorageKeyType,
    currentValue: T,
    setValueFunction: (value: T) => void,
    newValue: T | ((prevValue: T) => T)
  ) => {
    const updatedValue = typeof newValue === 'function' ? (newValue as (prevValue: T) => T)(currentValue) : newValue;
    ResultTableParameterStorageService.set(key, updatedValue);
    setValueFunction(updatedValue);
  };

  // Handlers for persisting table: sorting, pagination, visibility, filter, search, and search bar visibility
  const setSortingFunction = (newSortingValue: any) =>
    handleParameterChange(ResultSessionStorageKey.sorting, sorting, setSorting, newSortingValue);
  const setPaginationChangeFunction = (newPaginationValue: any) =>
    handleParameterChange(ResultSessionStorageKey.pagination, pagination, setPagination, newPaginationValue);
  const setFilterChangeFunction = (newFilterChangeValue: any) =>
    handleParameterChange(ResultSessionStorageKey.columnFilters, columnFilters, setColumnFilters, newFilterChangeValue);
  const setGlobalFilterChangeFunction = (newGlobalFilterChangeValue: string | ((prevValue: string) => string)) =>
    handleParameterChange(ResultSessionStorageKey.search, globalFilter, setGlobalFilter, newGlobalFilterChangeValue);
  const setShowGlobalFilterChangeFunction = (newShowGlobalFilterChangeValue: any) =>
    handleParameterChange(ResultSessionStorageKey.showSearch, showGlobalFilter, setShowGlobalFilter, newShowGlobalFilterChangeValue);



  return <MaterialReactTable
    columns={columns}
    data={rows}
    filterFns={filterFns}
    state={{
      isLoading,
      sorting,
      columnFilters,
      pagination,
      globalFilter,
      showGlobalFilter
    }}
    onSortingChange={setSortingFunction}
    onColumnFiltersChange={setFilterChangeFunction}
    onPaginationChange={setPaginationChangeFunction}
    onGlobalFilterChange={setGlobalFilterChangeFunction}
    onShowGlobalFilterChange={setShowGlobalFilterChangeFunction}
    defaultColumn={{
      minSize: 10,
      maxSize: 160,
      size: 10,
    }}
    enableColumnOrdering={true}
    positionActionsColumn="last"
    enableRowSelection
    selectAllMode="all"
    // enableRowActions
    initialState={{
      sorting,
      columnVisibility: { id: false },
      columnFilters,
      pagination,
      globalFilter,
      density: 'compact',
      columnPinning: { left: ['mrt-row-select'], right: ['mrt-row-actions'] },
    }}

    // TABLE STYLING IS IN THE THEME
    displayColumnDefOptions={{
      'mrt-row-actions': {
        header: "Actions", // Renames Actions Column
      },
      'mrt-row-select': {
        visibleInShowHideMenu: false, //hide the built-in row actions column from the show hide menu
      },
    }}

    muiTopToolbarProps={({ table }) => ({
      sx: (theme: any) => ({
        ...((theme.components?.MuiCustomToolbar?.styleOverrides?.root) || {}),
      }),
    })}

    muiTableHeadProps={{
      sx: (theme: any) => theme.components?.MuiTableHead?.styleOverrides?.root || {},
    }}

    muiTableHeadCellProps={({ column }) => ({
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableCell?.styleOverrides?.head || {}),
        ...(column.id === 'mrt-row-select' && {
          maxWidth: '50px',
          width: '50px',
        }),
      }),
    })}

    muiFilterTextFieldProps={{
      sx: (theme: any) => theme.components?.MuiFilterTextField?.styleOverrides?.root || {},
    }}

    muiTableBodyRowProps={({ row }) => ({
      onClick: (event) => {
        console.log(row.original);
        openPopup(row);
      },
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableBodyRow?.styleOverrides?.root || {}),
        backgroundColor:
          row.index % 2 === 0
            ? colorConfigs.tables.firstRow
            : colorConfigs.tables.secondRow,
      }),
    })}

    muiTableBodyProps={{
      sx: (theme: any) => theme.components?.MuiTableBody?.styleOverrides?.root || {},
    }}

    muiPaginationProps={{
      rowsPerPageOptions: [250, 500, 1000],
      sx: {
        padding: 0,
        margin: 0,
        '& .MuiTablePagination-root': {
          padding: 0,
          margin: 0,
        },
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          minHeight: '0px',
        },
        '& .MuiTablePagination-selectLabel': {
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          margin: 0,
        },
      },
    }}

    muiTableBodyCellProps={({ cell }) => ({
      // TABLE BODY CELLS
      sx: {
      },
    })}

    muiBottomToolbarProps={{
      // BOTTOM TOOLBAR (Pagination)
      sx: {
        height: '41px',
        maxHeight: '41px',
        minHeight: '41px',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        backgroundColor: '#fff',
        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
        margin: 0,
        padding: 0,
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          paddingBottom: 0,
          paddingTop: 0,
        },
        '& .MuiBox-root': {
          p: 0,
        },
        '& .MuiIconButton-root': {
          color: colorConfigs.tables.headBg,
        },
        '& .MuiTablePagination-root': {
          margin: 0,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          color: colorConfigs.buttons.bg,
        },
        '& .MuiTablePagination-selectLabel': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          color: colorConfigs.buttons.bg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
      },
    }}


    renderTopToolbarCustomActions={({ table }) => {

      const handleArchiveResults = () => {
        table.getSelectedRowModel().flatRows.map(row => {
          //Add archive logic
        });
      };

      const handleExportRows = async (rows: MRT_Row<any>[]) => {
        const resultIds = rows.map((row) => row.original.id);
        const payload = {
          result_ids: resultIds,
        };

        try {
          const url = `results/batch`;
          const response = await api.post(url, payload, {
            headers: {
              Authorization: `Bearer ${authData.access}`,
            },
          });

          const data = response.data.results;
          if (data.length < 1) {
            console.log("No results for this query");
            return;
          }

          console.log("Downloaded all results for one-line export:", data);

          // Prepare Excel rows
          const excelRows: any[] = [];
          const now = dayjs().format("YYYY-MM-DD HH:mm:ss");
          const filename = `${now}_ResultsList_OneLine`;

          data.forEach((result: any) => {
            const formattedTimestamp = dayjs(result.timestamp).format("YYYY-MM-DD HH:mm:ss") || "";
            const formattedVIReviewTimestamp = dayjs(result.visual_result?.reviewed_timestamp).format("YYYY-MM-DD HH:mm:ss") || "";

            // Create the base row
            const baseRow: any = {
              ID: result.id || "",
              Timestamp: formattedTimestamp,
              Longitude: result.longitude || "",
              Latitude: result.latitude || "",

              "Batch ID": result.batch_id || "",
              "Sample ID": result.sample_id || "",
              "Sample Type": result.sample_type || "",
              "Test Type": result.test_type || "",
              Notes: result.notes || "",
              "Organisation ID": result.organisation_id || '',
              "Organisation Name": result.organisation_name || '',
              "User ID": result.user_id,
              Type: result.type || '',
              "Aborted Reason": result.aborted_reason || '',
              "Interpretation": result.final_result_interpretation || "",

              "VISUAL RESULT": "",
              "Visual Interpretation": result.visual_result?.visual_interpretation || "",
              "App Interpretation": result.app_interpretation || "",
              "Visual Interpretation Reviewed?": result.visual_result?.is_reviewed === true ? "Yes" : result.visual_result?.is_reviewed === false ? "No" : "",
              "Visual Interpretation Notes": result.visual_result?.notes || "",
              "Visual Interpretation Reviewer": result.visual_result?.reviewer_id || "",
              "Reviewer Interpretation": result.visual_result?.reviewer_interpretation || "",
              "Visual Interpretation Reviewer Organisation": result.visual_result?.reviewer_organisation_id || "",
              "Visual Interpretation Review Timestamp": formattedVIReviewTimestamp,
              "Visual Interpretation Reviewer Notes": result.visual_result?.reviewer_notes || "",

              "IMAGES": "",
              "Full Image": result.full_image_path || "",
              "Strip Images": result.strip_image_paths || "",

              "DEVICE": "",
              "Device Platform": result.device_platform || "",
              "Device Manufacturer": result.device_manufacturer || "",
              "Device Model": result.device_model || "",
              "Device UUID": result.device_uuid || "",
              "OS Version": result.os_version || "",
              "App Version": result.app_version || "",
              "App Build": result.app_build || "",
              "Custom Fields": result.custom_fields || "",
            };

            // Update specific interpretation keys using interpretationLabels mapping // Borderline is now BORDERLINE
            baseRow["Interpretation"] =
              interpretationLabels[baseRow["Interpretation"] as keyof typeof interpretationLabels] || baseRow["Interpretation"];

            baseRow["Visual Interpretation"] =
              interpretationLabels[baseRow["Visual Interpretation"] as keyof typeof interpretationLabels] || baseRow["Visual Interpretation"];

            baseRow["App Interpretation"] =
              interpretationLabels[baseRow["App Interpretation"] as keyof typeof interpretationLabels] || baseRow["App Interpretation"];

            baseRow["Reviewer Interpretation"] =
              interpretationLabels[baseRow["Reviewer Interpretation"] as keyof typeof interpretationLabels] ||
              baseRow["Reviewer Interpretation"];


            // Add test strip details to the base row
            result.test_strips?.forEach((strip: any, stripIndex: number) => {
              const stripLabel = `Strip ${stripIndex + 1}`;
              // baseRow[stripLabel] = `Test Strip ${stripIndex + 1}`;
              baseRow[`${stripLabel} - CLine Score`] = strip.c_line?.score || "";
              // baseRow[`${stripLabel} - CLine Area`] = strip.c_line?.area || "";
              baseRow[`${stripLabel} - CLine Position`] = strip.c_line?.peak_position || "";
              baseRow[`${stripLabel} - Baseline`] = strip.baseline
                ? `[${strip.baseline.join(", ")}]`
                : "";
              baseRow[`${stripLabel} - Mean Profile`] = strip.profile
                ? `[${strip.profile.join(", ")}]`
                : "";
              // baseRow[`${stripLabel} - Baseline Fit Score`] = "N/A";

              // Add test lines for the strip
              const totalLines = strip.lines?.length ?? 0;

              strip.lines?.forEach((tLine: any, tLineIndex: number) => {
                const tLineNumber = totalLines - tLineIndex; // <-- count down
                const tLineLabel = `${stripLabel} - TLine ${tLineNumber}`;

                baseRow[`${tLineLabel} - Score`] = tLine.score || "";
                baseRow[`${tLineLabel} - Position`] = tLine.peak_position || "";
                baseRow[`${tLineLabel} - TC Ratio`] = tLine.tc_ratio || "";
              });
            });

            excelRows.push(baseRow);
          });

          tsXLXS().exportAsExcelFile(excelRows).saveAsExcelFile(filename);
        } catch (error) {
          console.error("Error exporting one-line rows:", error);
          alert("Failed to export results. Please try again.");
        }
      };

      return (
        <Box data-testid="Box-agsf" sx={{ display: 'flex', gap: '0.3rem', height: "40px", alignItems: 'flex-end' }}>
          <Tooltip data-testid="Tooltip-59t6" title="View action on selected data" placement="top" arrow>
            <Box data-testid="Box-6odx">
              <RareButton
                variant="outlined"
                onClick={handleClick}
                icon={<KeyboardArrowDownIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: isMobile ? 0 : 1, px: isMobile ? 1 : 2, fontSize: '14px' }}
              >
                {isMobile ? "" : "Bulk Actions"}
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-7bcy" title="Refresh Data" placement="top" arrow>
            <Box data-testid="Box-doq4">
              <RareButton
                variant="outlined"
                onClick={() => handleRefreshButton()}
                center={true}
                icon={<RefreshIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-euyd" title="Clear All Table Filters" placement="top" arrow>
            <Box data-testid="Box-bitm">
              <RareButton
                variant="outlined"
                onClick={() => handleRemoveAllFilters()}
                icon={<FilterAltOffOutlinedIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>
          <StyledMenu
            id="demo-customized-menu"
            MenuListProps={{
              'aria-labelledby': 'demo-customized-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <MenuItem disabled={
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            } onClick={() => handleExportRows(table.getSelectedRowModel().rows)} disableRipple>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FileDownloadOutlinedIcon />
                <Typography>Export to Excel</Typography>
              </Box>
            </MenuItem>
            <MenuItem
              disabled={
                !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
              }
              onClick={() => onBulkReview(table.getSelectedRowModel().rows)}
              disableRipple
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PlaylistAddCheckIcon />
                <Typography>Bulk review</Typography>
              </Box>
            </MenuItem>
          </StyledMenu>
        </Box>
      );
    }}
  />
}

export default ResultsTable;

